package co.maytes.standupbot.dashboard.pages.app.onboarding

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.SkeletonCard
import co.maytes.standupbot.dashboard.components.feedback.SkeletonLine
import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.Spinner
import co.maytes.standupbot.dashboard.components.widgets.UpgradeNudge
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideCalendar
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideClock
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideMessagesSquare
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideUsers
import co.maytes.standupbot.dashboard.features.onboarding.BrandChip
import co.maytes.standupbot.dashboard.features.onboarding.ChipSize
import co.maytes.standupbot.dashboard.features.onboarding.FactRow
import co.maytes.standupbot.dashboard.features.onboarding.FlowNode
import co.maytes.standupbot.dashboard.features.onboarding.NoticeTone
import co.maytes.standupbot.dashboard.features.onboarding.OnboardingShell
import co.maytes.standupbot.dashboard.features.onboarding.OnboardingStep
import co.maytes.standupbot.dashboard.features.onboarding.SchedulePresets
import co.maytes.standupbot.dashboard.features.onboarding.Seal
import co.maytes.standupbot.dashboard.features.onboarding.WizardBrand
import co.maytes.standupbot.dashboard.features.onboarding.WizardFlow
import co.maytes.standupbot.dashboard.features.onboarding.WizardList
import co.maytes.standupbot.dashboard.features.onboarding.WizardNotice
import co.maytes.standupbot.dashboard.features.onboarding.rememberOnboardingStore
import co.maytes.standupbot.dashboard.features.standupruns.RunStatusRules
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.shared.dto.common.PagedResponse
import co.maytes.standupbot.shared.dto.standuprun.StandupRunStatusDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunSummaryDto
import co.maytes.standupbot.shared.dto.standuprun.TriggerStandupRunResponseDto
import co.maytes.standupbot.shared.roster.LinkReportText
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import io.ktor.client.request.header
import kotlin.random.Random
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private const val POLL_INTERVAL_MS = 2_000L
private const val POLL_BUDGET_MS = 30_000L

private sealed interface PostPhase {
    data object Idle : PostPhase

    data object Posting : PostPhase

    data class Watching(val run: StandupRunSummaryDto?) : PostPhase

    data class Settled(val run: StandupRunSummaryDto) : PostPhase

    data class TimedOut(val run: StandupRunSummaryDto?) : PostPhase
}

private fun StandupRunStatusDto.isTerminal(): Boolean = !RunStatusRules.isWorking(this)

@Page("/app/onboarding/preview")
@Composable
fun OnboardingPreviewPage() {
    val api = rememberApiClient()
    val auth = rememberAuthStore()
    val ui = rememberUiStore()
    val store = rememberOnboardingStore()
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()

    var phase by remember { mutableStateOf<PostPhase>(PostPhase.Idle) }
    var watchToken by remember { mutableStateOf(0) }
    var baselineRunId by remember { mutableStateOf<String?>(null) }
    var finishing by remember { mutableStateOf(false) }
    var errorText: String? by remember { mutableStateOf(null) }

    LaunchedEffect(Unit) {
        if (store.createdStandupId == null) {
            ctx.router.navigateTo(OnboardingStep.Setup.path)
        }
    }

    val standupId = store.createdStandupId
    val standupName = store.createdStandupName ?: store.standupName
    val workspaceId = auth.currentUser?.workspace?.id
    val channelLabel = store.selectedChannelName?.let { "#$it" } ?: "your channel"
    val clock = SchedulePresets.clockLabel(store.scheduleHour, store.scheduleMinute)

    LaunchedEffect(watchToken) {
        if (watchToken == 0 || workspaceId == null || standupId == null) return@LaunchedEffect
        var waitedMs = 0L
        var latest: StandupRunSummaryDto? = null
        while (waitedMs < POLL_BUDGET_MS) {
            val result = api.get<PagedResponse<StandupRunSummaryDto>>(
                "/api/v1/workspaces/$workspaceId/standups/$standupId/runs?limit=1",
            )
            if (result is Result.Ok) {
                val run = result.value.items.firstOrNull()?.takeIf { it.id != baselineRunId }
                if (run != null) {
                    latest = run
                    store.setPreviewRunId(run.id)
                    if (run.status.isTerminal()) {
                        phase = PostPhase.Settled(run)
                        return@LaunchedEffect
                    }
                    phase = PostPhase.Watching(run)
                }
            }
            delay(POLL_INTERVAL_MS)
            waitedMs += POLL_INTERVAL_MS
        }
        phase = PostPhase.TimedOut(latest)
    }

    fun finish(destination: String) {
        scope.launch {
            finishing = true
            when (val r = auth.markOnboardingComplete()) {
                is Result.Ok -> ctx.router.navigateTo(destination)
                is Result.Err -> {
                    ui.showToast("Couldn't finish onboarding: ${r.error.displayMessage}", ToastKind.Error)
                    finishing = false
                }
            }
        }
    }

    fun postNow() {
        if (workspaceId == null || standupId == null) return
        scope.launch {
            phase = PostPhase.Posting
            errorText = null
            baselineRunId = store.previewRunId
            val key = "onboarding-${Random.nextLong().toString(16)}"
            val res = api.post<EmptyBody, TriggerStandupRunResponseDto>(
                path = "/api/v1/workspaces/$workspaceId/standups/$standupId/trigger",
                body = EmptyBody,
            ) {
                header("Idempotency-Key", key)
            }
            when (res) {
                is Result.Ok -> {
                    phase = PostPhase.Watching(null)
                    watchToken++
                }
                is Result.Err -> {
                    phase = PostPhase.Idle
                    errorText = res.error.displayMessage
                    ui.showToast(
                        "Couldn't post the first standup: ${res.error.displayMessage}",
                        ToastKind.Error,
                    )
                }
            }
        }
    }

    val current = phase
    val posted = current !is PostPhase.Idle && current !is PostPhase.Posting
    val idle = current == PostPhase.Idle
    val flowing = current is PostPhase.Posting || current is PostPhase.Watching || current is PostPhase.TimedOut
    val settled = current is PostPhase.Settled

    OnboardingShell(
        step = OnboardingStep.Preview,
        headline = "Post your first standup",
        subline = "Posts to $channelLabel now, from the last 24 hours of Linear activity.",
        art = {
            WizardFlow(
                nodes = listOf(
                    FlowNode(WizardBrand.Linear, live = settled),
                    FlowNode(WizardBrand.Claude, live = settled),
                    FlowNode(WizardBrand.Slack, live = settled),
                ),
                flowing = flowing,
            )
        },
        nextEnabled = if (posted) !finishing else idle && workspaceId != null && standupId != null && !finishing,
        nextLoading = current == PostPhase.Posting || finishing,
        nextLabel = when {
            finishing -> "Finishing…"
            current == PostPhase.Posting -> "Posting…"
            posted -> "Finish"
            else -> "Post it now"
        },
        onNext = if (posted) {
            { finish("/app/onboarding/done") }
        } else {
            { postNow() }
        },
        skipLabel = if (idle) "Skip, wait until $clock" else null,
        onSkip = if (idle && !finishing) {
            { finish("/app/onboarding/done") }
        } else {
            null
        },
    ) {
        StateSwap(state = current.bodyKey()) {
            when (current) {
                PostPhase.Idle, PostPhase.Posting ->
                    ReadyView(
                        channelLabel = channelLabel,
                        schedule = "${SchedulePresets.describeDays(store.scheduleDays)} at $clock",
                        rosterSummary = store.createdRoster?.let { LinkReportText.summary(it.linkReport) },
                        errorText = errorText,
                        nudge = { store.createdNudge?.let { UpgradeNudge(it) } },
                        onRetry = { postNow() },
                    )
                is PostPhase.Watching -> WatchingView(current.run, channelLabel)
                is PostPhase.Settled ->
                    SettledView(
                        run = current.run,
                        channelLabel = channelLabel,
                        busy = finishing,
                        onOpenRun = { finish("/app/runs/${current.run.id}") },
                    )
                is PostPhase.TimedOut ->
                    TimedOutView(
                        run = current.run,
                        busy = finishing,
                        onKeepWaiting = {
                            phase = PostPhase.Watching(current.run)
                            watchToken++
                        },
                        onOpenRun = current.run?.let { run -> { finish("/app/runs/${run.id}") } },
                    )
            }
        }
    }
}

private fun PostPhase.bodyKey(): String = when (this) {
    PostPhase.Idle, PostPhase.Posting -> "ready"
    is PostPhase.Watching -> "watching"
    is PostPhase.Settled -> "settled"
    is PostPhase.TimedOut -> "timed-out"
}

@Composable
private fun ReadyView(
    channelLabel: String,
    schedule: String,
    rosterSummary: String?,
    errorText: String?,
    nudge: @Composable () -> Unit,
    onRetry: () -> Unit,
) {
    Div(attrs = { classes("sb-wiz-stack") }) {
        nudge()
        WizardList {
            FactRow(label = "Channel", value = channelLabel, icon = { LucideMessagesSquare(size = 18) })
            FactRow(label = "Schedule", value = schedule, icon = { LucideClock(size = 18) })
            rosterSummary?.let { FactRow(label = "Roster", value = it, icon = { LucideUsers(size = 18) }) }
            FactRow(label = "Source", value = "Last 24 hours", icon = { LucideCalendar(size = 18) })
        }
        errorText?.let {
            WizardNotice(
                tone = NoticeTone.Danger,
                title = "Couldn't post the standup",
                message = it,
                action = { Button(label = "Retry", variant = ButtonVariant.Secondary, onClick = onRetry) },
            )
        }
    }
}

@Composable
private fun WatchingView(run: StandupRunSummaryDto?, channelLabel: String) {
    Div(attrs = { classes("sb-wiz-stack"); attr("role", "status") }) {
        Div(attrs = { classes("sb-wiz-conn") }) {
            BrandChip(WizardBrand.Slack, ChipSize.Md)
            Div(attrs = { classes("sb-wiz-conn-text") }) {
                Span(attrs = { classes("sb-wiz-conn-title") }) { Text("Posting to $channelLabel") }
                Span(attrs = { classes("sb-wiz-conn-detail") }) {
                    Text(if (run == null) "Waiting for the run to start" else "Generating the standup")
                }
            }
            if (run == null) Spinner(ariaLabel = "Generating the standup") else Badge(label = run.status.label(), tone = run.status.tone())
        }
        SkeletonCard {
            Div(attrs = { classes("sb-wiz-stack") }) {
                SkeletonLine(widthPct = 38, heightPx = 14)
                SkeletonLine(widthPct = 100, heightPx = 12)
                SkeletonLine(widthPct = 88, heightPx = 12)
                SkeletonLine(widthPct = 66, heightPx = 12)
            }
        }
        Span(attrs = { classes("sb-wiz-row-note") }) { Text("Usually 4 to 8 seconds.") }
    }
}

@Composable
private fun SettledView(
    run: StandupRunSummaryDto,
    channelLabel: String,
    busy: Boolean,
    onOpenRun: () -> Unit,
) {
    val tone = run.status.noticeTone()
    Div(attrs = { classes("sb-wiz-stack"); attr("role", "status") }) {
        if (tone == NoticeTone.Success) {
            Div(attrs = { classes("sb-wiz-hero") }) {
                Seal(large = true)
                Span(attrs = { classes("sb-wiz-conn-title") }) { Text(run.status.sentence(channelLabel)) }
                Badge(label = run.status.label(), tone = run.status.tone())
            }
        } else {
            WizardNotice(tone = tone, title = run.status.label(), message = run.status.sentence(channelLabel))
        }
        Div(attrs = { classes("sb-wiz-actions") }) {
            Button(label = "Open the run", variant = ButtonVariant.Secondary, enabled = !busy, onClick = onOpenRun)
            run.slackMessageUrl?.let {
                Link(path = it, text = "View in Slack", modifier = Modifier.color(Palette.accent))
            }
        }
    }
}

@Composable
private fun TimedOutView(
    run: StandupRunSummaryDto?,
    busy: Boolean,
    onKeepWaiting: () -> Unit,
    onOpenRun: (() -> Unit)?,
) {
    Div(attrs = { classes("sb-wiz-stack"); attr("role", "status") }) {
        WizardNotice(
            tone = NoticeTone.Info,
            title = "Taking longer than usual",
            message = "The standup is still being generated. Keep waiting, or finish now and find it under Runs.",
        )
        Div(attrs = { classes("sb-wiz-actions") }) {
            Button(label = "Keep waiting", variant = ButtonVariant.Secondary, enabled = !busy, onClick = onKeepWaiting)
            onOpenRun?.let { Button(label = "Open the run", variant = ButtonVariant.Ghost, enabled = !busy, onClick = it) }
            run?.let { Badge(label = it.status.label(), tone = it.status.tone()) }
        }
    }
}

private fun StandupRunStatusDto.noticeTone(): NoticeTone =
    when (this) {
        StandupRunStatusDto.POSTED, StandupRunStatusDto.DRAFTED -> NoticeTone.Success
        StandupRunStatusDto.FAILED -> NoticeTone.Danger
        else -> NoticeTone.Info
    }

private fun StandupRunStatusDto.label(): String =
    when (this) {
        StandupRunStatusDto.PENDING -> "Pending"
        StandupRunStatusDto.GENERATING -> "Generating"
        StandupRunStatusDto.DRAFTED -> "Draft"
        StandupRunStatusDto.POSTED -> "Posted"
        StandupRunStatusDto.FAILED -> "Failed"
        StandupRunStatusDto.NO_ACTIVITY -> "No activity"
        StandupRunStatusDto.SKIPPED -> "Skipped"
    }

private fun StandupRunStatusDto.tone(): BadgeTone =
    when (this) {
        StandupRunStatusDto.POSTED -> BadgeTone.Success
        StandupRunStatusDto.FAILED -> BadgeTone.Error
        StandupRunStatusDto.NO_ACTIVITY, StandupRunStatusDto.SKIPPED -> BadgeTone.Warning
        StandupRunStatusDto.PENDING, StandupRunStatusDto.GENERATING -> BadgeTone.Neutral
        StandupRunStatusDto.DRAFTED -> BadgeTone.Accent
    }

private fun StandupRunStatusDto.sentence(channelLabel: String): String =
    when (this) {
        StandupRunStatusDto.POSTED -> "Your first standup is in $channelLabel."
        StandupRunStatusDto.FAILED -> "The run failed. Open it to see why."
        StandupRunStatusDto.NO_ACTIVITY ->
            "No Linear activity matched the roster in the last 24 hours, so a quiet-day note went to $channelLabel."
        StandupRunStatusDto.SKIPPED -> "The run was skipped."
        StandupRunStatusDto.DRAFTED -> "Your first standup is drafted and will post at the scheduled time."
        StandupRunStatusDto.PENDING, StandupRunStatusDto.GENERATING -> "Generating…"
    }

@kotlinx.serialization.Serializable
private object EmptyBody
